<template>
  <!-- In a switched context: a one-click entry that returns to the original
       admin via an empty POST (backend reads us/oc from the JWT). -->
  <v-list-item
    v-if="revertName"
    @click="revertSwitch()"
    :disabled="switchLoading"
  >
    <template #prepend>
      <v-icon :icon="Icons.ACCOUNT_ARROW_LEFT" />
    </template>
    <v-list-item-title>
      {{ t('navigation.userSwitchBack', {name: revertName}) }}
    </v-list-item-title>
  </v-list-item>
  <v-dialog
    v-else-if="canSwitch"
    v-model="dialog"
    width="500"
    persistent
    :aria-label="t('user.switch.label')"
  >
    <template #activator="{ props }">
      <v-list-item
        v-bind="props"
        @click="loadUserList()"
      >
        <template #prepend>
          <v-icon :icon="Icons.ACCOUNT_SWITCH" />
        </template>
        <v-list-item-title>
          {{ t('navigation.userSwitch') }}
        </v-list-item-title>
      </v-list-item>
    </template>
    <v-card>
      <v-card-title>
        {{ t('user.switch.label') }}
      </v-card-title>
      <v-card-text v-if="userList.length > 0">
        <v-autocomplete
          v-model="user"
          :items="userList"
          item-value="id"
          item-title="name"
          :label="t('user.switch.user')"
          class="mt-10"
          variant="outlined"
        />
        <template v-if="customerList.length > 0">
          <v-autocomplete
            v-model="customer"
            :items="customerList"
            item-value="id"
            item-title="name"
            :disabled="customerList.length === 0"
            :label="t('user.switch.customer')"
            variant="outlined"
          />
        </template>
      </v-card-text>
      <v-skeleton-loader
        v-else
        type="text@2"
        class="pa-4"
      />
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="default"
          @click="close()"
        >
          {{ t('common.close') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="user === '' || customer === ''"
          @click="switchUser()"
          :loading="switchLoading"
        >
          {{ t('user.switch.label') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
// ==================== [CSS] ===============================

// ==================== [JS] ================================
import {UserSwitchApi} from '@/common/api/user/UserSwitchApi';
import {SecUtil} from '@/common/utils/security/SecUtil';
// ==================== [VUE COMPONENTS] ====================
import {computed, ref, watch} from 'vue';
import {ErrorDialog, Icons, verifyResponse} from '@simpleforyou/client-core';
import {useRouter} from 'vue-router';
import {IUserSwitchCustomer, IUserSwitchUser} from '@/types/components/admin/IUserSwitch';
import {useI18n} from 'vue-i18n';
import {useApiClient} from '@/common/api/useApiClient';
import {useStore} from '@/common/store/store';
import {BaseApi} from '@/common/api/BaseApi';
import {Routes} from '@/common/enums/Routes';
import {LoginUser} from '@/types/LoginUser';
import {Permissions} from '@simpleforyou/coaching-shared';
import {clearUserSwitchTarget, saveUserSwitchTarget} from '@/common/auth/userSwitchState';
import {reloadPage} from '@/common/utils/windowUtils';

const {t} = useI18n();
const store = useStore();
const router = useRouter();
const apiClient = useApiClient();

const dialog = ref<boolean>(false);
const user = ref<string>();
const userList = ref<IUserSwitchUser[]>([]);
const customer = ref<string>();
const customerList = ref<IUserSwitchCustomer[]>([]);
// The forward switch needs the switch role. The revert ("back to admin") must
// NOT: a switched session often runs as a role-less user, and after a reload
// store.roles is empty for that user — yet the backend authorizes the revert
// purely from the JWT us/oc claims. So gate the revert entry on being in a
// switched session (user_switch_name present), independent of the role.
const canSwitch = computed(() => SecUtil.hasRoles(Permissions.ROLE_USER_SWITCH_INDEX));
const revertName = computed(() => store.userData?.user_switch_name);
const switchLoading = ref<boolean>(false);

const close = () => {
  user.value = '';
  userList.value = [];
  customer.value = '';
  customerList.value = [];
  dialog.value = false;
};
const loadUserList = async () => {
  const response = await apiClient.get<IUserSwitchUser[]>(UserSwitchApi.api()).catch(() => {
    new ErrorDialog();
  });

  if (verifyResponse(response, true)) {
    userList.value = response.data;
  }
};

const loadCustomerList = async () => {
  customerList.value = [];
  if (user.value === '') {
    return null;
  }
  const response = await apiClient.get<IUserSwitchCustomer[]>(UserSwitchApi.api({id: user.value})).catch(() => {
    new ErrorDialog();
  });

  if (verifyResponse(response, true)) {
    customerList.value = response.data;
  }
};

const switchUser = async () => {
  switchLoading.value = true;
  const response = await apiClient.post<LoginUser>(UserSwitchApi.api(), {
      user: user.value,
      customer: customer.value,
    }
  ).catch(() => {
    new ErrorDialog();
    switchLoading.value = false;
  });

  if (verifyResponse(response, true)) {
    // Persist the target so the switch is re-exchanged after a browser reload
    // (the switch token itself is access-only and lives only in memory).
    saveUserSwitchTarget({user: user.value as string, customer: customer.value as string});
    store.setUserData(response.data.user);
    store.setAccessToken(response.data.accessToken);
    clearApiCache();
    await router.push({name: Routes.LOGIN});
  }
};

// On user switch discard the cached GET responses of the previous context,
// otherwise the new session would show the old data.
const clearApiCache = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(BaseApi.ROOT_URL))
    .forEach((key) => sessionStorage.removeItem(key));
};

// The backend reads us/oc from the JWT and ignores the body — a POST without
// payload is enough to revert to the original admin.
const revertSwitch = async () => {
  switchLoading.value = true;
  const response = await apiClient.post<LoginUser>(UserSwitchApi.api(), {}).catch(() => {
    new ErrorDialog();
    switchLoading.value = false;
  });

  if (verifyResponse(response, true)) {
    // Back to admin → drop the durable switch intent so reloads stay on admin,
    // set the admin token, then fully re-bootstrap the app so ALL permissions/
    // roles/modules reload cleanly from /user-data (a partial in-place restore
    // could otherwise leave the admin without their full rights).
    clearUserSwitchTarget();
    store.setAccessToken(response.data.accessToken);
    clearApiCache();
    reloadPage();
  }
};

watch(user, () => {
  customer.value = undefined;
  loadCustomerList();
});
</script>

<style scoped>

</style>
